import { Download, Loader2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadMediaFile } from "@/components/common/media-preview/downloadMediaFile";
import { isAccessUrlFresh } from "@/components/common/media-preview/utils";
import { toast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { triggerBrowserDownload } from "@/lib/utils/mediaFile";

interface DownloadActionProps {
  guid: string;
  fileName: string;
  downloadUrl?: string | null;
  useDirectDownload?: boolean;
  accessUrlExpiresAt?: number | null;
  className?: string;
}

export const DownloadAction: React.FC<DownloadActionProps> = ({
  guid,
  fileName,
  downloadUrl,
  useDirectDownload = false,
  accessUrlExpiresAt,
  className,
}) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isDownloading) return;

    const directUrlIsFresh = isAccessUrlFresh(accessUrlExpiresAt);
    if (downloadUrl && useDirectDownload && directUrlIsFresh) {
      triggerBrowserDownload({ href: downloadUrl, fileName });
      return;
    }

    setIsDownloading(true);
    try {
      await downloadMediaFile({ guid, fileName });
    } catch {
      toast({
        title: t("common.error"),
        description: t("common.errorWhileLoading"),
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  return (
    <button
      type="button"
      className={cn("inline-flex items-center justify-center", className)}
      title={t("common.download")}
      aria-label={t("common.downloadWithName", { name: fileName })}
      disabled={isDownloading}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {isDownloading ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Download className="size-4" />
      )}
    </button>
  );
};
